import React from "react";
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import TextField from "@mui/material/TextField";
import Guid from "_shared/guids/guid";

const MessageField = styled(TextField)(({ theme }) => ({
  minWidth: theme.spacing(40),
  width: "100%"
}));
const ButtonContainer = styled("div")(() => ({
  width: "100%"
}));
const MessagePaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
}));
/**
 * A `Popper` has no `z-index` of its own, and this one opens inside the reviewer selector's `Popover` —
 * a `MuiModal-root` at `theme.zIndex.modal` with an invisible backdrop stretched over the viewport.
 * Painting at `auto` therefore put the whole editor UNDER that backdrop: the textarea still took
 * typing (it holds focus from mount) but every click on OK hit the backdrop instead, which closed the
 * selector and discarded the message — a dead end for anyone who used a mouse.
 */
const MessagePopper = styled(Popper)(({ theme }) => ({
  zIndex: theme.zIndex.modal + 1,
}));

type ReviewerMessagePopoverProps = {
  className?: string;
  anchorEl: HTMLElement | null;
  userId?: Guid;
  message?: string;
  onCanceled: () => void;
  onMessageChanged: (userId?: Guid, message?: string) => void;
};

/**
 * The custom-message editor for one reviewer, anchored to that reviewer's row.
 *
 * A `Popper`, deliberately, where this used to be a `Popover`. A `Popover` is a `Modal`, and this one
 * opens from inside the reviewer selector's own `Popover` — so `ModalManager` marked the outer popover
 * `aria-hidden`/`inert` while the focus was still inside it, which the browser refuses:
 *
 *   Blocked aria-hidden on an element because its descendant retained focus.
 *
 * That is not a warning to silence: hiding a subtree that holds focus is exactly the state that strands
 * a screen-reader user on an element their reader has been told does not exist. A `Popper` is not a
 * modal — it never enlists `ModalManager`, so the reviewer list it opens over stays visible to assistive
 * technology, which is also the truth of it, since that list stays interactive underneath.
 *
 * Dismissal, focus and stacking order therefore have to be supplied here, being what `Modal` was
 * providing: click-away and Escape close it, the field takes focus on open so keyboard users land in
 * the editor rather than being left behind on the row's icon button, and `MessagePopper` lifts it above
 * the selector's own backdrop.
 */
export default function ReviewerMessagePopover(props: Readonly<ReviewerMessagePopoverProps>) {
  const {
    className,
    anchorEl,
    userId,
    onCanceled,
    onMessageChanged
  } = props;
  const open = Boolean(anchorEl);

  // Safe as an initializer only because the caller mounts one of these per opening (see the `key` on
  // its usage): this state is seeded once, at the moment the editor appears for a given reviewer.
  const [message, setMessage] = React.useState<string>(props.message ?? '');

  // A stable callback ref, so it runs on mount rather than on every commit — an inline one would be
  // called again on each render and steal the caret back to the start while someone was typing.
  // Preferred over `autoFocus`, which is blanket-banned for the usability reasons that do not apply
  // here: this editor exists only because the user just asked for it.
  const focusOnMount = React.useCallback((node: HTMLTextAreaElement | null) => {
    node?.focus();
  }, []);

  return (
    <MessagePopper className={className} open={open} anchorEl={anchorEl} placement="bottom-end">
      <ClickAwayListener onClickAway={() => onCanceled()}>
        <MessagePaper
          elevation={8}
          onKeyDown={(event) => {
            if (event.key === "Escape") onCanceled();
          }}
        >
          <MessageField
            inputRef={focusOnMount}
            placeholder="Message To Reviewer (Optional)"
            value={message}
            multiline
            minRows={3}
            onChange={event => setMessage(event.target.value)}
          />
          <ButtonContainer>
            <Button
              color="primary"
              onClick={() => onMessageChanged(userId, message)}
            >
              OK
            </Button>
            <Button
              color="primary"
              onClick={() => onCanceled()}
            >
              Cancel
            </Button>
          </ButtonContainer>
        </MessagePaper>
      </ClickAwayListener>
    </MessagePopper>
  );
}
